import i18n from "@/localization/i18n";
import { login, logout } from "@/redux/authorization/authorization.slice";
import { setActiveAccount } from "@/redux/activeAccount/activeAccount.slice";
import { setSuccess } from "@/redux/request/request.slice";
import { userApi } from "@/redux/user/user.api";
import type { RootState } from "@/redux";
import type { ApiEntities, ApiEntity } from "@/types/redux.type";
import type { IUser } from "@/types/user.type";
import {
  Action,
  isFulfilled,
  isRejectedWithValue,
  Middleware,
  MiddlewareAPI
} from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import * as Sentry from "@sentry/react";
import { checkEntraAuthGate, logStatsigEvent } from "@/utils/statsig";
import { debug } from "@/utils/debug";
import { msalInstance } from "@/msal/msal-instance";
import { clearEntraLoginHint } from "@/msal/login-hint";
import { t } from "i18next";
import { toast, ToastOptions } from "react-toastify";

const isFulfilledWithValue = isFulfilled();
const GENERAL_ERROR_CODE = -1;

// TODO: Refactor - come up with a better middleware setup (more TS friendly)
// TODO: Refactor - come up with a better error handling of queries and mutations (should be here, but more customizable and efficient)
const endpointsToSkipError = [
  "getGISUploadData",
  "getUploadDMAs",
  "createProject",
  "updateProject",
  "createInvitedUsers",
  "validateInvitationToken",
  "acceptInvitation",
  "exportViewConsumptionProfiling",
  "exportViewWaypointJobs",
  "exportViewRelays",
  "exportViewSessions",
  "exportViewWaypointLogs",
  "exportViewWaypoints"
];

const defaultErrorToastOptions: ToastOptions = {
  autoClose: 3500,
  closeOnClick: true,
  pauseOnHover: false,
  draggable: true,
  progress: undefined
};

export const resetState = () => ({
  type: "RESET_STATE"
});

const setSentryUserFromProfile = (profile: IUser | undefined) => {
  if (!profile) return;

  const userId = profile.id;
  const email = profile.email;
  const hasUserId =
    typeof userId === "string" ||
    (typeof userId === "number" && !Number.isNaN(userId));

  if (!hasUserId && typeof email !== "string") return;

  Sentry.setUser({
    id: hasUserId ? String(userId) : undefined,
    email: typeof email === "string" ? email : undefined
  });
};

const rtkQueryErrorHandler: Middleware =
  (store: MiddlewareAPI) => (next) => (action: Action) => {
    const isLoginAction = action.type === login.type;
    const wasAuthorized = isLoginAction
      ? (store.getState() as RootState).auth.isAuthorized
      : false;

    if (action.type === logout.type) {
      debug.log("[middleware] logout action");
      logStatsigEvent("portal_sign_out");
      Sentry.setUser(null);
      store.dispatch(setActiveAccount(null));
      store.dispatch(resetState());
      store.dispatch(userApi.util.resetApiState());

      clearEntraLoginHint();

      if (checkEntraAuthGate()) {
        const account = msalInstance.getActiveAccount();

        if (account) {
          debug.log(
            "[middleware] Entra gate on → logoutRedirect username=%s",
            account.username
          );
          void msalInstance.logoutRedirect({
            account,
            postLogoutRedirectUri: "/"
          });
          return next(action);
        }

        debug.log(
          "[middleware] Entra gate on but no MSAL account — skip logoutRedirect"
        );
      }
    }

    if (isRejectedWithValue(action)) {
      const endpointName = (action?.meta?.arg as { endpointName: string })
        ?.endpointName;

      const payload = action.payload as FetchBaseQueryError;
      const payloadData = (payload.data || {}) as Record<
        string,
        Record<string, unknown>
      >;
      const statusCode = payload?.status || GENERAL_ERROR_CODE;

      // General error handling for all endpoints apart from exceptions
      if (!endpointsToSkipError.includes(endpointName) && statusCode !== 401) {
        if (typeof statusCode === "number" && payloadData.detail) {
          if (typeof payloadData.detail === "string") {
            toast.error(payloadData.detail, {
              ...defaultErrorToastOptions
            });
          } else if (typeof payloadData.detail.message === "string") {
            toast.error(payloadData.detail.message, {
              ...defaultErrorToastOptions
            });
          } else {
            const tKey = `SERVER_ERRORS.${statusCode}`;
            const message = i18n.exists(tKey)
              ? i18n.t(tKey)
              : i18n.t("SERVER_ERRORS.-1");

            toast.error(message, {
              ...defaultErrorToastOptions,
              toastId: "general-error"
            });
          }
        }
      }

      const isLoginEndpoint = endpointName === "login";

      // Custom error handling for specific endpoints
      switch (endpointName) {
        case "updateProject":
          if (payloadData.detail) {
            if (statusCode === 418) {
              toast.error(
                t(`SERVER_ERRORS.${payloadData.detail.id as number}`, {
                  projectNames: (
                    (payloadData.detail.project_names as string[]) || []
                  ).join(", "),
                  dmaKeys: (
                    (payloadData.detail.dma_keys as string[]) || []
                  ).join(", ")
                }),
                {
                  autoClose: false
                }
              );
            } else if (typeof payloadData.detail.message === "string") {
              toast.error(payloadData.detail.message, {
                ...defaultErrorToastOptions
              });
            } else {
              toast.error(t("SERVER_ERRORS.-1"), {
                ...defaultErrorToastOptions
              });
            }
          }
          break;

        default:
          break;
      }

      if (!isLoginEndpoint && statusCode === 401) {
        const isEntra = checkEntraAuthGate();
        debug.log("[middleware] 401 received", {
          endpoint: endpointName,
          isEntra
        });

        if (isEntra) {
          debug.log(
            "[middleware] 401 on Entra path — MsalAuthManager will handle token refresh"
          );
        } else if (isEntra === null) {
          debug.log(
            "[middleware] 401 received but Statsig not ready — deferring to bootstrap"
          );
        } else {
          debug.log(
            "[middleware] 401 legacy path — dispatching logout + toast"
          );
          store.dispatch(logout());

          toast.error(t("ERROR.SESSION_EXPIRED"), {
            ...defaultErrorToastOptions,
            toastId: "session-expired"
          });
        }
      }
    } else if (isFulfilledWithValue(action)) {
      const type = (action.meta.arg as { endpointName: string })?.endpointName;
      if (type) {
        store.dispatch(setSuccess(type));
      }

      if (type === "getProfile") {
        debug.log("[middleware] getProfile fulfilled — syncing Sentry user");
        setSentryUserFromProfile(action.payload as IUser);
      }
    }

    const result = next(action);

    // Fires only on an actual sign-in transition, not on background refreshes.
    // (MsalAuthManager dispatches login() on every silent token refresh.)
    if (isLoginAction) {
      const isAuthorizedNow = (store.getState() as RootState).auth.isAuthorized;
      if (!wasAuthorized && isAuthorizedNow) {
        debug.log("[middleware] login action — logging portal_sign_in");
        logStatsigEvent("portal_sign_in");
      }
    }

    return result;
  };

export const initialEntities: ApiEntities = {
  reducers: {},
  middlewares: [rtkQueryErrorHandler]
};

const getApiEntitiesCallback = <T extends ApiEntity>(
  data: ApiEntities,
  item: T
) => ({
  ...data,
  reducers: {
    ...data.reducers,
    [item.reducerPath]: item.reducer
  },
  middlewares: [...data.middlewares, item.middleware]
});

export const getApiEntities = <T extends ApiEntity>(list: T[]) =>
  list.reduce<ApiEntities>(getApiEntitiesCallback, initialEntities);
